import i18next from 'i18next';
import { ErrorConverter } from '../errorHandling/ErrorConverter';
import { HttpResponse } from '../interfaces';
import ApiFaultCodes from './api.fault.constants';
import {
  ApiFault,
  ApiFaultOrWarnings,
  MicroserviceFault
} from './api.fault.interfaces';

/** Return a translated error message from a runtime, non-API fault error */
export function getUnknownFaultErrorMessage() {
  return i18next.t(ApiFaultCodes.UNKNOWN);
}

/** Interpolation values for the final render of a fault message. */
export interface FaultInterpolationValues {
  [variable: string]: string | (() => string);
}

/**
 * Flatten `Fault.ContextData` pairs into interpolation values so fault
 * translations can reference them (e.g. `{{ProductName}}`).
 *
 * This is the last interpolation pass for a fault message, so any placeholder
 * the API left out of `ContextData` renders empty instead of deferring to a
 * later pass that will never come.
 */
export function getFaultContextValues(
  fault?: ApiFault
): FaultInterpolationValues {
  return (fault?.ContextData || []).reduce<FaultInterpolationValues>(
    (values, { Key, Value }) => (Key ? { ...values, [Key]: Value } : values),
    { missingInterpolationHandler: () => '' }
  );
}

/** Return a translated error message from an API fault or warning. */
export function getFaultErrorMessage(fault: ApiFault) {
  const code = `${fault?.Code || ''}`;
  const codeAndSubcode = ErrorConverter.getFaultCode(fault);
  const contextValues = getFaultContextValues(fault);

  if (codeAndSubcode && i18next.exists(codeAndSubcode)) {
    return i18next.t(codeAndSubcode, contextValues);
  }

  if (code && i18next.exists(code)) {
    return i18next.t(code, contextValues);
  }

  return `${i18next.t(ApiFaultCodes.UNKNOWN)}${
    codeAndSubcode ? ` - ${codeAndSubcode}` : ''
  }`;
}

/** Return a translated error message for Core or Microservice error responses */
export function getCoreOrMicroserviceFaultErrorMessage(
  errorResponse: HttpResponse<ApiFaultOrWarnings | MicroserviceFault>
) {
  const isApiFault = Number.isInteger(
    (errorResponse.data as ApiFaultOrWarnings)?.Fault?.Code
  );
  return getFaultErrorMessage(
    isApiFault
      ? (errorResponse.data as ApiFaultOrWarnings)?.Fault
      : {
          Code: (errorResponse.data as MicroserviceFault).Code.Value,
          Message: (errorResponse.data as MicroserviceFault).Detail
        }
  );
}
